/**
 * @fileoverview `MARKETING_I18N_NAMESPACES` — the namespace registry for a PUBLIC marketing portal.
 * @package @puffin-core/i18n-namespaces
 *
 * AAS-I18N-002 ruling 39 (2026-08-10).
 *
 * ## This is a SEPARATE registry, and the separation is measured rather than assumed
 *
 * A marketing portal is not a smaller authenticated app; it mounts a different set of shared
 * chrome. Measured on 2026-08-10 across all nine marketing portals:
 *
 * - **`login` — YES.** `MarketingWebsiteApp.jsx` renders `<LoginShell>` and `MarketingLoginPage`
 *   renders `<MarketingCrossDomainLogin>`, both from `@puffin-core/login-shell`. Every portal's
 *   bootstrap already passed `loadLoginStrings` by hand for exactly this reason — without it the
 *   marketing `/login` renders English in every locale.
 * - **`captcha` — YES.** Every marketing portal renders `<CaptchaField>` (3–4 sites each: demo
 *   request, contact, newsletter). Under the old wiring `loadCaptchaStrings` was called by **zero**
 *   apps and the package self-registered from a render to compensate.
 * - **`formSecurity` — YES** (2026-09-24). `<RateLimitNotice>` renders inside every portal's
 *   `/login` form and on throttled public forms.
 * - **`phoneUi` and `legalContent` — YES** (2026-10-23, PACKAGE-I18N-SEAT U3). pos-web's `/demo` form
 *   renders `<PhoneInput>`, and every portal's `/privacy` and `/terms` render `<LegalDocumentPage>`.
 * - **`shell` — NO.** Portals opt out explicitly (they previously passed `shellLocales: []`); they
 *   mount `@puffin-core/marketing-shell` chrome, not `page-shell`.
 * - **`onboarding` — NO.** The setup guide and help drawer are authenticated surfaces.
 *
 * ## Why not one registry with the unused entries harmlessly present
 *
 * Because "harmless" would be a bundle claim, not a correctness claim. Importing
 * `APP_I18N_NAMESPACES` into a marketing portal statically references `loadShellStrings`, which
 * drags `@puffin-core/page-shell` into a PUBLIC page's payload. Marketing pages are the ones with a
 * performance budget that a real user on a phone pays. Two registries state the two surfaces'
 * genuinely different needs, and this package's `exports` map offers them as separate subpaths so
 * the split does not depend on a bundler successfully tree-shaking a const.
 *
 * @module @puffin-core/i18n-namespaces/marketingNamespaces
 */

// 🔴 The narrow `/strings` SUBPATH, never the barrel — see `appNamespaces.js` for the measurement.
// It matters most HERE: a marketing portal is a PUBLIC page with a real performance budget, and a
// barrel import would have put `CaptchaField` + `@marsidev/react-turnstile` in its boot chunk.
import {
  ACCOUNT_APPS_UI_NAMESPACE,
  SUPPORTED_LOCALES as ACCOUNT_APPS_UI_SUPPORTED_LOCALES,
  loadAccountAppsUiStrings,
} from '@puffin-core/account-apps-ui/strings';
import {
  AUTH_SHELL_NAMESPACE,
  SUPPORTED_LOCALES as AUTH_SHELL_SUPPORTED_LOCALES,
  loadAuthShellStrings,
} from '@puffin-core/auth-shell/strings';
import {
  CAPTCHA_NAMESPACE,
  SUPPORTED_LOCALES as CAPTCHA_SUPPORTED_LOCALES,
  loadCaptchaStrings,
} from '@puffin-core/captcha-ui/strings';
import {
  FORM_SECURITY_NAMESPACE,
  SUPPORTED_LOCALES as FORM_SECURITY_SUPPORTED_LOCALES,
  loadFormSecurityStrings,
} from '@puffin-core/form-security/strings';
import {
  LOGIN_NAMESPACE,
  SUPPORTED_LOCALES as LOGIN_SUPPORTED_LOCALES,
  loadLoginStrings,
} from '@puffin-core/login-shell/strings';

import {
  MFA_NAMESPACE,
  SUPPORTED_LOCALES as MFA_SUPPORTED_LOCALES,
  loadMfaStrings,
} from '@puffin-core/mfa-ui/strings';
import {
  MARKETING_SHELL_NAMESPACE,
  SUPPORTED_LOCALES as MARKETING_SHELL_SUPPORTED_LOCALES,
  loadMarketingShellStrings,
} from '@puffin-core/marketing-shell/strings';
import {
  PUBLIC_TOOLS_NAMESPACE,
  SUPPORTED_LOCALES as PUBLIC_TOOLS_SUPPORTED_LOCALES,
  loadPublicToolsStrings,
} from '@puffin-core/public-tools/strings';
import {
  PHONE_UI_NAMESPACE,
  SUPPORTED_LOCALES as PHONE_UI_SUPPORTED_LOCALES,
  loadPhoneUiStrings,
} from '@puffin-core/phone-ui/strings';
import {
  LEGAL_CONTENT_NAMESPACE,
  SUPPORTED_LOCALES as LEGAL_CONTENT_SUPPORTED_LOCALES,
  loadLegalContentStrings,
} from '@puffin-core/legal-content/strings';
import {
  ERROR_HANDLING_NAMESPACE,
  SUPPORTED_LOCALES as ERROR_HANDLING_SUPPORTED_LOCALES,
  loadErrorHandlingStrings,
} from '@puffin-core/error-handling/strings';

import { defineNamespace, defineNamespaceRegistry } from '@puffin-core/i18n-bootstrap';

/**
 * The shared namespaces a public marketing portal renders.
 *
 * @constant {ReadonlyArray<Object>}
 */
export const MARKETING_I18N_NAMESPACES = defineNamespaceRegistry(
  [
    defineNamespace({
      namespace: LOGIN_NAMESPACE,
      supportedLocales: LOGIN_SUPPORTED_LOCALES,
      register: loadLoginStrings,
    }),
    defineNamespace({
      namespace: CAPTCHA_NAMESPACE,
      supportedLocales: CAPTCHA_SUPPORTED_LOCALES,
      register: loadCaptchaStrings,
    }),
    // ⚠️ `mfa` belongs here and `reAuth` does NOT, and the difference is reachability, not
    // symmetry. `<MfaVerification>` is reached from a PUBLIC login screen —
    // `LoginShell → LoginForm → form.mfaRequired` — so omitting it would leave marketing `/login`
    // MFA in English, which is the exact bug this change closes. `@puffin-core/re-auth` is
    // authenticated-only (`createStepUpAuth`, `ManagerVerificationPrompt`) AND depends on
    // `@puffin-core/page-shell`, which this registry exists to keep out of a public payload.
    //
    // Safe because the `/strings` subpath is LEAF-ONLY: `en.js`, `es.js`, `namespace.js`,
    // `resolveString.js`, no component. Importing the package barrel here instead would put the
    // whole MFA UI in every marketing boot chunk — the measured `@marsidev/react-turnstile`
    // failure recorded above, repeated.
    defineNamespace({
      namespace: MFA_NAMESPACE,
      supportedLocales: MFA_SUPPORTED_LOCALES,
      register: loadMfaStrings,
    }),
    // `formSecurity` (Q-UMB-21, 2026-09-24) — reachable from BOTH public surfaces a portal has: the
    // `/login` form (`LoginShell → LoginForm → <RateLimitNotice>`, throttled by default since
    // Q-UMB-20) and the throttled public forms (prep-web's demo request). The `/strings` subpath
    // is leaf-only — two frozen bundles, a constant and a resolver — so no component rides in.
    defineNamespace({
      namespace: FORM_SECURITY_NAMESPACE,
      supportedLocales: FORM_SECURITY_SUPPORTED_LOCALES,
      register: loadFormSecurityStrings,
    }),
    // `authShell` (L-R7CLOSE row 32, 2026-09-25) — the public `/login` form's reset modal
    // (`LoginShell → PasswordResetModal`) renders auth-shell's `<PasswordRequirementList>`, the ONE
    // password checklist, whose requirement names live in this namespace. Leaf-only `/strings`.
    defineNamespace({
      namespace: AUTH_SHELL_NAMESPACE,
      supportedLocales: AUTH_SHELL_SUPPORTED_LOCALES,
      register: loadAuthShellStrings,
    }),
    // `marketingShell` (LIFE-SHORTLISTED-001 F-Q7-1, 2026-09-28) — the words marketing-shell renders
    // on every marketing site: the cookie banner, the skip link, the menu button, the switchers and
    // the copyright line. The nine sites each carried their own copy (the banner said four different
    // things); the `/strings` subpath is leaf-only, so no component rides in.
    defineNamespace({
      namespace: MARKETING_SHELL_NAMESPACE,
      supportedLocales: MARKETING_SHELL_SUPPORTED_LOCALES,
      register: loadMarketingShellStrings,
    }),
    // `publicTools` (LINKABLE-ASSETS-001 C1, 2026-10-13) — the words the public reference tools render on a
    // marketing portal's `/tools/…` pages: the breadcrumb landmark, the provenance stamp with its two
    // disclaimers, the calculator's chrome and refusals. The `/strings` subpath is leaf-only — two frozen
    // bundles, a constant and a resolver — so no component (and no page-shell subpath) rides in.
    defineNamespace({
      namespace: PUBLIC_TOOLS_NAMESPACE,
      supportedLocales: PUBLIC_TOOLS_SUPPORTED_LOCALES,
      register: loadPublicToolsStrings,
    }),
    // `accountAppsUi` (APP-ENTITLEMENT-001 Phase 11) — the catalog's WORDS: a size dimension's unit ("per location
    // a month"), a consumable ("AI minutes"), an anchor's included features, "free with your anchor", the share of
    // recovery. The marketing pricing section on every site renders the same catalog rows the Billing page does, so
    // it reads the same words from the same bundle; a second copy in the marketing namespace would be the drift
    // TERM-001 exists to end. The `/strings` subpath is leaf-only — two frozen bundles, a constant and a resolver —
    // so neither the Billing page nor its Stripe form rides into a public payload.
    defineNamespace({
      namespace: ACCOUNT_APPS_UI_NAMESPACE,
      supportedLocales: ACCOUNT_APPS_UI_SUPPORTED_LOCALES,
      register: loadAccountAppsUiStrings,
    }),
    // `phoneUi` (#51, JSDOC-PROP-CONTRACT-001 PACKAGE-I18N-SEAT U3, 2026-10-23) — the phone field's country
    // picker. pos-web's public `/demo` form (`DemoPage → DemoSignupForm → SignupFormContent`) renders
    // `<PhoneInput>` on THIS instance, so without it the picker's names and search box stay English on a
    // Spanish form. The `/strings` subpath is leaf-only — libphonenumber stays out of the boot chunk.
    defineNamespace({
      namespace: PHONE_UI_NAMESPACE,
      supportedLocales: PHONE_UI_SUPPORTED_LOCALES,
      register: loadPhoneUiStrings,
    }),
    // `legalContent` (#56, same unit) — the chrome around a legal document (the table of contents, the
    // effective-date line, the contact block, the subprocessor row labels, the unavailable notice). Every
    // marketing portal's `/privacy` and `/terms` render `<LegalDocumentPage>` on this instance. The
    // documents stay English by design; the `/strings` subpath carries no document.
    defineNamespace({
      namespace: LEGAL_CONTENT_NAMESPACE,
      supportedLocales: LEGAL_CONTENT_SUPPORTED_LOCALES,
      register: loadLegalContentStrings,
    }),
    // `errorHandling` (#57, D1 errorCode unit 4) — the error-to-notification mapper's titles. A marketing portal
    // mounts `<NotificationProvider>` (pos-web's `/demo` form), whose error logger titles a notification through the
    // ONE mapper, which carries KEYS into #57 and translates them with the portal's own instance. Without #57 here, a
    // portal's error notification would show a raw key. The `/strings` subpath is leaf-only: two frozen bundles, a
    // constant and a resolver, none of the package's handlers.
    defineNamespace({
      namespace: ERROR_HANDLING_NAMESPACE,
      supportedLocales: ERROR_HANDLING_SUPPORTED_LOCALES,
      register: loadErrorHandlingStrings,
    }),
  ],
  'MARKETING_I18N_NAMESPACES',
);

export default MARKETING_I18N_NAMESPACES;
